import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { ConfirmationState } from '../types';

interface AdminContextValue {
  confirmationDialog: ConfirmationState | null;
  showConfirmation: (message: string, onConfirm: () => void) => void;
  hideConfirmation: () => void;
}

const AdminContext = createContext<AdminContextValue | null>(null);

interface Props {
  children: React.ReactNode;
}

export function AdminProvider({ children }: Props) {
  const [confirmationDialog, setConfirmationDialog] =
    useState<ConfirmationState | null>(null);

  const hideConfirmation = useCallback(() => {
    setConfirmationDialog(null);
  }, []);

  const showConfirmation = useCallback(
    (message: string, onConfirm: () => void) => {
      setConfirmationDialog({
        message,
        onConfirm: () => {
          onConfirm();
          hideConfirmation();
        },
        onCancel: hideConfirmation,
      });
    },
    [hideConfirmation]
  );

  const value = useMemo(
    () => ({
      confirmationDialog,
      showConfirmation,
      hideConfirmation,
    }),
    [confirmationDialog, showConfirmation, hideConfirmation]
  );

  return (
    <AdminContext.Provider value={value}>{children}</AdminContext.Provider>
  );
}

export function useAdminContext() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdminContext must be used within AdminProvider');
  }
  return context;
}
